import { Link, useLocation } from "react-router-dom";
import { Home, Search, MessageCircle, PawPrint, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/**
 * Sticky bottom navigation surfaced on small screens only.
 * Mounted globally in App; renders nothing on desktop or for auth/onboarding routes.
 */
const HIDE_ON = [
  "/login",
  "/signup",
  "/vet-signup",
  "/forgot-password",
  "/reset-password",
  "/mfa",
  "/auth/callback",
  "/consultation",
];

const ITEMS = [
  { to: "/", label: "Home", icon: Home, match: (p: string) => p === "/" },
  { to: "/vet-directory", label: "Search", icon: Search, match: (p: string) => p.startsWith("/vet-directory") || p.startsWith("/find-vets") },
  { to: "/messages", label: "Messages", icon: MessageCircle, match: (p: string) => p.startsWith("/messages") },
  { to: "/my-dashboard", label: "Pets", icon: PawPrint, match: (p: string) => p.startsWith("/my-dashboard") || p.startsWith("/my-passports") || p.startsWith("/pet/") },
  { to: "/settings", label: "Account", icon: User, match: (p: string) => p.startsWith("/settings") || p.startsWith("/notifications") },
];

const MobileBottomNav = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useAuth();
  if (!isMobile) return null;
  if (HIDE_ON.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) return null;

  return (
    <nav
      aria-label="Primary"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.25rem)" }}
      className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-background/95 backdrop-blur-md border-t border-border"
    >
      <ul className="grid grid-cols-5">
        {ITEMS.map(({ to, label, icon: Icon, match }) => {
          const active = match(location.pathname);
          const target = !user && (to === "/messages" || to === "/my-dashboard") ? "/login" : to;
          return (
            <li key={to}>
              <Link
                to={target}
                style={{ WebkitTapHighlightColor: "transparent" }}
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-all duration-150",
                  "active:scale-[0.92] active:bg-primary/5",
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )}
                aria-current={active ? "page" : undefined}
              >
                <Icon className={cn("w-5 h-5 transition-transform", active && "scale-110")} aria-hidden />
                <span>{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
